import type { DeepReadonly, InjectionKey, Ref } from 'vue'
import type { AccessRole } from '../AccessRole'
import type { AccessRoleWithPermissions } from '../AccessRoleWithPermissions'
import type { AccessCode } from '../AccessCode'
import type { UserInRole } from '../UserInRole'
import type { UserRoleAssignment } from '../UserRoleAssignment'
import type {
  AddRolePermissionsResponse,
  ChangeAccessRoleNameResponse,
  GetAccessControlAccessRolesResponse,
  GetAccessRoleResponse,
  GetAccessRolesRequestParams,
  RemoveRolePermissionsResponse,
  GetUserRoleAssignmentsRequestParams,
  AddUserRoleAssignmentRequestParams,
  AddUserRoleAssignmentResponse,
  RemoveUserRoleAssignmentRequestParams,
  RemoveUserRoleAssignmentResponse
} from '../api-requests/AccessControlRequests'

/**
 * Data source for the collection of all access roles (`AccessRole[]`).
 *
 * App-section-scoped and shared across features (custom-fields,
 * tenant-users). For a single role's permissions/users detail, use
 * {@link AccessRoleDetailService} instead.
 */
export interface AccessRolesCollectionService {
  accessRoles: Ref<AccessRole[]>
  addAccessRole(accessRole: AccessRole): void
  changeAccessRoleName(
    roleId: string,
    name: string,
    description: string
  ): Promise<AccessRole | undefined>
  removeAccessRole(id: string): void
  clearData(options?: { unsubscribeFromEvents?: boolean }): void
  fetchAccessRoles(
    params?: GetAccessRolesRequestParams & {
      forceFetch?: boolean
      subscribeToEvents?: boolean
    }
  ): Promise<GetAccessControlAccessRolesResponse | undefined>
  subscribeToAccessRolesEvents(): () => void
  unsubscribeFromAccessRolesEvents(): void
}

/**
 * Data source for a single access role's detail
 * (`AccessRoleWithPermissions` + the users assigned to it).
 *
 * Page-scoped to the role permissions view. Depends on the collection
 * service ({@link AccessRolesCollectionService}) to keep the shared list
 * in sync on name changes.
 */
export interface AccessRoleDetailService {
  accessRole: Ref<AccessRoleWithPermissions | undefined>
  usersWithAccessRole: DeepReadonly<Ref<UserInRole[]>>
  addRolePermissions(
    permissionCodes: AccessCode[]
  ): Promise<AddRolePermissionsResponse | undefined>
  removeRolePermissions(
    permissionCodes: string[]
  ): Promise<RemoveRolePermissionsResponse | undefined>
  changeAccessRoleName(
    roleId: string,
    name: string,
    description: string
  ): Promise<ChangeAccessRoleNameResponse | AccessRole | undefined>
  clearData(options?: { unsubscribeFromEvents?: boolean }): void
  fetchAccessRole(
    roleId: string,
    options?: {
      forceFetch?: boolean
      subscribeToEvents?: boolean
    }
  ): Promise<GetAccessRoleResponse | undefined>
  fetchUsersWithAccessRole(roleId: string): Promise<void>
}

export const accessRolesCollectionKey = Symbol(
  'gs-access-roles-collection'
) as InjectionKey<AccessRolesCollectionService>

export const accessRoleDetailKey = Symbol(
  'gs-access-role-detail'
) as InjectionKey<AccessRoleDetailService>

/**
 * Data source for a single user's role assignments. Built on the
 * access-control api/signalR, so it lives here (the tenant-users sub-domain
 * folds this datasource into `@gat-ship/access-control`). Runtime-free: the
 * concrete `useUserRoleAssignments` composable holds a SignalR handle
 * internally that is not part of the returned object.
 */
export interface UserRoleAssignmentsService {
  roleAssignments: Ref<UserRoleAssignment[]>
  fetchRoleAssignments(
    params: GetUserRoleAssignmentsRequestParams & {
      forceFetch?: boolean
      subscribeToEvents?: boolean
    }
  ): Promise<void>
  commands: {
    addRoleAssignment(
      params: AddUserRoleAssignmentRequestParams
    ): Promise<AddUserRoleAssignmentResponse>
    removeRoleAssignment(
      params: RemoveUserRoleAssignmentRequestParams
    ): Promise<RemoveUserRoleAssignmentResponse>
  }
}

export const userRoleAssignmentsKey = Symbol(
  'gs-user-role-assignments'
) as InjectionKey<UserRoleAssignmentsService>
